import { Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import ThemedModal from "../../../@ui/Modal/ThemedModal";
import { StyledModalTitle } from "../../../@ui/StyledModalTitle";
import { createRef, useEffect, useState } from "react";
import { Order } from "../../../types/Order/Order";
import { useOrderService, useUserAdminService } from "../../../hooks";
import { AsyncTypeahead } from "react-bootstrap-typeahead";
import Typeahead from "react-bootstrap-typeahead/types/core/Typeahead";

interface AssignOrderModalProps {
  orderId: string;
  onAssign: (userId: string) => void;
  onClose: () => void;
}

const AssignOrderModal = ({
  orderId,
  onAssign,
  onClose,
}: AssignOrderModalProps) => {
  const orderService = useOrderService();
  const userAdminService = useUserAdminService();
  const [order, setOrder] = useState<Order | undefined>();
  const [orgName, setOrgName] = useState<string | undefined>();
  const [userId, setUserId] = useState<string | undefined>();
  const [orderLoading, setOrderLoading] = useState(false);
  const [orgOptions, setOrgOptions] = useState<any[]>([]);
  const [orgLoading, setOrgLoading] = useState(false);
  const [userOptions, setUserOptions] = useState<any[]>([]);
  const [userLoading, setUserLoading] = useState(false);
  const userSearchControl = createRef<Typeahead>();

  useEffect(() => {
    if (!orderId) return;
    const abortController = new AbortController();
    setOrderLoading(true);
    orderService
      .GetOrderById(orderId, abortController)
      .then((order) => {
        setOrder(order);
        setOrderLoading(false);
      })
      .catch(() => {});

    return () => {
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const orgSearch = (searchTerm: string) => {
    setOrgLoading(true);
    userAdminService
      .searchOrganisations(searchTerm)
      .then((data) => {
        setOrgOptions(data);
      })
      .catch(() => {})
      .finally(() => {
        setOrgLoading(false);
      });
  };

  const userSearch = (searchTerm: string) => {
    setUserLoading(true);
    userAdminService
      .listUsers({
        pagination: { page: 1, pageSize: 10 },
        filterExpression: `OrganisationName eq ${orgName} _and FullName cn ${searchTerm}`,
      })
      .then((data) => {
        setUserOptions(
          data.records.map((u) => ({
            id: u.userId,
            name: `${u.firstName} ${u.lastName}`,
          })),
        );
      })
      .catch(() => {})
      .finally(() => {
        setUserLoading(false);
      });
  };

  const orgChange = (selected: any[]) => {
    if (selected.length > 0) {
      setOrgName(selected[0].name);
      userSearchControl.current?.focus();
    }
  };

  const userChange = (selected: any[]) => {
    if (selected.length > 0) {
      const value = selected[0].id;
      setUserId(value);
    }
  };

  return (
    <ThemedModal centered scrollable show={true}>
      <Modal.Header className="bg-secondary text-white">
        <StyledModalTitle>Assign Order To Customer</StyledModalTitle>
      </Modal.Header>
      <Modal.Body className="overflow-auto">
        {orderLoading && (
          <div className="d-flex justify-content-center">
            <Spinner />
          </div>
        )}
        {order && (
          <>
            <Row className="mb-3 g-0">
              <Col md="2">Created: </Col>
              <Col>{order.createdDate.toFormat("dd/MM/yyyy HH:mm")}</Col>
            </Row>
            <p className="mb-0">Ordered Items</p>
            <ul>
              {order.lineItems.map((li) => (
                <li key={li.id}>{li.description}</li>
              ))}
            </ul>
            <Form.Group className="w-100">
              <Form.Label className="mb-0">Organisation</Form.Label>
              <AsyncTypeahead
                className="w-100"
                id="org"
                isLoading={orgLoading}
                onSearch={orgSearch}
                labelKey="name"
                options={orgOptions}
                onChange={orgChange}
                placeholder="Type to search organisation name"
                positionFixed={true}
              />
            </Form.Group>

            <Form.Group className="w-100">
              <Form.Label className="mb-0">User</Form.Label>
              <AsyncTypeahead
                ref={userSearchControl}
                className="w-100"
                id="user"
                isLoading={userLoading}
                onSearch={userSearch}
                labelKey="name"
                options={userOptions}
                onChange={userChange}
                placeholder="Type to search user name"
                disabled={!orgName}
                positionFixed={true}
              />
            </Form.Group>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="success"
          disabled={!userId}
          onClick={() => onAssign(userId!)}>
          Assign
        </Button>
        <Button variant="danger" onClick={onClose}>
          Cancel
        </Button>
      </Modal.Footer>
    </ThemedModal>
  );
};

export default AssignOrderModal;
